import { useMemo, useState } from "react";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

export default function ReviewFeedbackForm({
  value,
  onChange,
  productName = "",
  maxChars = 2000,
  maxPhotos = 5,
  showButtonLabel = "Write a review",
  submitLabel = "Submit review",
}) {
  const [expanded, setExpanded] = useState(false);
  const [info, setInfo] = useState("");
  const base = useMemo(() => (value && typeof value === "object" ? value : {}), [value]);
  const reviews = useMemo(() => (Array.isArray(base.reviews) ? base.reviews : []), [base.reviews]);
  const model = useMemo(() => {
    return {
      rating: Number(base.rating || base.draft?.rating) || 0,
      review: String(base.review || base.draft?.review || ""),
      photos: Array.isArray(base.photos) ? base.photos : Array.isArray(base.draft?.photos) ? base.draft.photos : [],
      reviewerName: String(base.reviewerName || base.draft?.reviewerName || ""),
      agree: base.agree === true || base.draft?.agree === true,
    };
  }, [base]);

  const emit = (patch) => {
    const draft = { ...model, ...patch };
    onChange?.({ ...base, ...draft, draft, reviews });
  };

  const onPickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allowed = maxPhotos - model.photos.length;
    const next = files.slice(0, Math.max(0, allowed));
    if (!next.length) {
      setInfo(`You can upload up to ${maxPhotos} photos.`);
      return;
    }
    Promise.all(
      next.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve({ name: file.name, src: String(reader.result || "") });
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          })
      )
    ).then((arr) => {
      const merged = [...model.photos, ...arr.filter(Boolean)].slice(0, maxPhotos);
      emit({ photos: merged });
      setInfo("");
    });
  };

  const onSubmit = () => {
    if (!model.rating) return setInfo("Please select a rating.");
    if (!model.review.trim()) return setInfo("Please write your review.");
    if (!model.reviewerName.trim()) return setInfo("Please enter your name.");
    if (!model.agree) return setInfo("Please accept policy and terms to submit.");
    const nextReview = {
      rating: model.rating,
      review: model.review.trim(),
      reviewerName: model.reviewerName.trim(),
      photos: model.photos,
      submittedAt: new Date().toISOString(),
    };
    onChange?.({
      ...base,
      reviews: [nextReview, ...reviews],
      rating: 0,
      review: "",
      photos: [],
      reviewerName: "",
      agree: false,
      draft: { rating: 0, review: "", photos: [], reviewerName: "", agree: false },
    });
    setExpanded(false);
    setInfo("Review submitted successfully.");
  };

  const count = model.review.length;
  const safeCount = clamp(count, 0, maxChars);
  const avgRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / reviews.length).toFixed(1)
    : "0.0";
  const starCounts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => Number(r.rating) === star).length,
  }));
  const renderStars = (rating) => {
    const n = Number(rating) || 0;
    return "★★★★★".slice(0, n) + "☆☆☆☆☆".slice(0, 5 - n);
  };
  const toDisplayName = (name) => {
    const clean = String(name || "").trim();
    if (!clean) return "Anonymous";
    const parts = clean.split(/\s+/);
    if (parts.length === 1) return `${parts[0]}.`;
    return `${parts[0]} ${parts[1].charAt(0)}.`;
  };

  return (
    <div className="lfx-review-form">
      <div className="lfx-review-summary-block">
        <div className="lfx-review-summary-head">
          <div className="lfx-review-summary-title">Customer Reviews</div>
          {!expanded ? (
            <button type="button" className="lfx-btn primary sm" onClick={() => setExpanded(true)}>
              {showButtonLabel}
            </button>
          ) : null}
        </div>
        <div className="lfx-review-summary-layout">
          <div className="lfx-review-summary-left">
            <div className="lfx-review-summary-rating">{avgRating}</div>
            <div className="lfx-review-summary-stars">{renderStars(Math.round(Number(avgRating)))}</div>
            <div className="lfx-review-summary-count">{reviews.length} reviews</div>
          </div>
          <div className="lfx-review-breakdown">
            {starCounts.map((row) => {
              const pct = reviews.length ? Math.round((row.count / reviews.length) * 100) : 0;
              return (
                <div key={`star-${row.star}`} className="lfx-review-breakdown-row">
                  <span className="lfx-review-breakdown-label">{row.star} Star</span>
                  <span className="lfx-review-breakdown-bar">
                    <span className="lfx-review-breakdown-fill" style={{ width: `${pct}%` }} />
                  </span>
                  <span className="lfx-review-breakdown-value">{row.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {expanded ? (
        <>
          <div className="lfx-review-title">Write a review</div>
          <div className="lfx-review-product">{productName || "<Product name>"}</div>

          <label className="lfx-label">Rating (5 STAR clickable)</label>
          <div className="lfx-review-stars" role="radiogroup" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`lfx-review-star ${model.rating >= star ? "active" : ""}`}
                onClick={() => emit({ rating: star })}
                aria-label={`${star} star`}
              >
                ★
              </button>
            ))}
          </div>

          <label className="lfx-label">Review</label>
          <textarea
            className="lfx-input lfx-textarea"
            value={model.review}
            maxLength={maxChars}
            onChange={(e) => emit({ review: e.target.value })}
            placeholder="Share your feedback with us now"
            rows={5}
          />
          <div className="lfx-review-counter">{safeCount}/{maxChars} chars</div>

          <label className="lfx-label">Add photo (upload control)</label>
          <div className="lfx-review-upload-row">
            <input type="file" accept="image/*" multiple onChange={onPickPhotos} />
            <span className="lfx-review-upload-plus">+</span>
            <span className="lfx-review-upload-count">{model.photos.length}/{maxPhotos}</span>
          </div>
          {model.photos.length ? (
            <div className="lfx-review-thumb-row">
              {model.photos.map((p, i) => (
                <img key={`${p.name}-${i}`} src={p.src} alt={p.name} className="lfx-review-thumb" />
              ))}
            </div>
          ) : null}

          <label className="lfx-label">Your name</label>
          <input
            className="lfx-input"
            value={model.reviewerName}
            onChange={(e) => emit({ reviewerName: e.target.value })}
            placeholder="Your name"
          />

          <label className="lfx-checkbox-label" style={{ marginTop: 8 }}>
            <input
              className="lfx-checkbox"
              type="checkbox"
              checked={model.agree}
              onChange={(e) => emit({ agree: e.target.checked })}
            />
            <span className="lfx-review-terms">
              By submitting this review, you agree to Trustoo.io&apos;s Privacy Policy and Terms regarding the processing of your personal data.
            </span>
          </label>

          <div className="lfx-review-actions">
            <button type="button" className="lfx-btn primary" onClick={onSubmit}>
              {submitLabel}
            </button>
          </div>
        </>
      ) : null}
      {reviews.length ? (
        <div className="lfx-review-list">
          {reviews.slice(0, 5).map((r, i) => (
            <div key={`${r.reviewerName}-${r.submittedAt}-${i}`} className="lfx-review-list-item">
              <div className="lfx-review-list-stars">{renderStars(r.rating)}</div>
              <div className="lfx-review-list-head">
                <strong>{toDisplayName(r.reviewerName)}</strong>
                <span className="lfx-review-verified">verified ✔</span>
              </div>
              <div className="lfx-review-list-text">{r.review}</div>
            </div>
          ))}
        </div>
      ) : null}
      {info ? <div className="lfx-review-info">{info}</div> : null}
    </div>
  );
}
